import React from 'react';
import { Box } from '@mui/system';
import { Button, CircularProgress, Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AssetDetail } from '../../graphql/queries/asset';
import { Asset } from '../../codegen/graphql';
import AssetSearchResult from '../RegisterAssetModal/assetSearchResult';
import RegisterAssetModal from '../RegisterAssetModal';

interface ReplacementAssetPickerRendererProps {
    attribute: { index: string };
    value: string | null | undefined;
    error?: string;
    touched?: boolean;
    setFieldValue: (field: string, value: any) => void;
    sourceAssetId?: string;
    /** Outer wizard's bridge: when set, the inner create-asset form's submit is
     *  registered here so the wizard's Next click can fire it. */
    submitTriggerRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

const ReplacementAssetPicker = ({ attribute, value, error, touched, setFieldValue, sourceAssetId, submitTriggerRef }: ReplacementAssetPickerRendererProps) => {
    const { t } = useTranslation();

    const { data, loading } = useQuery(AssetDetail, {
        variables: {
            filter: { id: { equals: value } },
            imageCrop: 'fit',
            imageWidth: 200,
            imageHeight: 200,
        },
        skip: !value,
    });

    if (!value) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, maxWidth: '100%' }}>
                <RegisterAssetModal
                    isModal={false}
                    // Await the outer Formik setFieldValue (Formik returns a Promise that
                    // settles after the state update) so the outer wizard's bridge sees
                    // the value populated before its post-bridge validation runs.
                    onAssetSelected={async (id) => {
                        await (setFieldValue as any)(attribute.index, id);
                    }}
                    excludeAssetId={sourceAssetId}
                    submitTriggerRef={submitTriggerRef}
                />
                {touched && error && (
                    <Typography level="body-sm" sx={{ color: 'danger.500' }}>
                        {error}
                    </Typography>
                )}
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const asset = (data?.asset as Asset | null | undefined) ?? null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, maxWidth: '100%' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {t('Selected replacement asset')}
            </Typography>
            {asset
                ? <AssetSearchResult asset={asset} selected />
                : (
                    <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                        {t('Could not load asset details.')}
                    </Typography>
                )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    onClick={() => setFieldValue(attribute.index, undefined)}
                >
                    {t('Change')}
                </Button>
            </Box>
        </Box>
    );
};

export default ReplacementAssetPicker;
